import { type User } from '@gql/graphql';

/**
 * Minimal shape authorization needs from a viewer. Callers may hold a
 * narrower query result than the full generated `User` type (e.g. `GetMeQuery`
 * doesn't select every `User` field) — authorization only ever reads `admin`,
 * so it should not demand the full entity.
 */
export type AuthorizableUser = Pick<User, 'admin'>;

/**
 * Any authorization strategy has to confirm to this protocol
 */
interface AuthorizationStrategy {
    isAuthorized: (viewer: AuthorizableUser) => boolean;
}

/**
 * A authorization strategy that verifies if the user is an admin
 */
export class AdminAuthorizationStrategy implements AuthorizationStrategy {
    isAuthorized(viewer: AuthorizableUser): boolean {
        return viewer.admin;
    }
}

/**
 * Check if the user is authorized according the provided strategy.
 * @param {AuthorizableUser} viewer - The user to authorize.
 * @param {AuthorizationStrategy} strategy - The strategy to use.
 *
 * @returns {Boolean} if the user is authorized, false otherwise.
 */
export function isUserAuthorized(
    viewer: AuthorizableUser,
    strategy: AuthorizationStrategy,
): boolean {
    return strategy.isAuthorized(viewer);
}

/**
 * Check if the user is an admin.
 * Uses the "AdminAuthorizationStrategy" to verify that the user is an admin.
 */
export function isUserAdmin(viewer: AuthorizableUser): boolean {
    return isUserAuthorized(viewer, new AdminAuthorizationStrategy());
}
